// Libraries
import { useState } from "react";

// Components
import { StartTransferRequest } from "./StartTransferRequest";
import TransferRequestAction from "./TransferRequestAction";

// Types
import { TTransferType } from "../../types";
import { Box, Typography } from "@mui/material";

// Helpers
import { getTenantParameters } from "@/Common/helpers/Tenant";

// Hooks
import { useLocations } from "@/Common/hooks/Location/useLocations";

import SwapRequest from "./SwapRequest";
import { useMutation } from "@tanstack/react-query";
import { IInventoryTradeVehicles, IMappedTransferRequest, TSearchTransferRequest } from "../../types/dealerTrades";
import { createTransferRequestStatus } from "@/DataPages/api/inventory/mutations";
import { useInventoryContext } from "../../contexts/InventoryFilterContext";
import { resolveDefaultTransferRequest } from "../../helpers";
import useUniqueVinNumbers from "../../hooks/useUniqueVinNumbers";
import { InventoryTab } from "../../constants";

type TTransferRequestProps = {
	locationId: string;
	commissionCycleId?: string;
	payPeriodId: string;
	editPermission: boolean;
	isCurrentPayPeriod: boolean;
};

export default function TransferRequest(props: Readonly<TTransferRequestProps>) {
	const { locationId, editPermission, isCurrentPayPeriod } = props;
	const [transferType, setTransferType] = useState<TTransferType>("");
	const { filterItems } = useInventoryContext();
	const uniqueVinNumbers = useUniqueVinNumbers(InventoryTab.DEALER_TRADES);

	const { mutate, isPending } = useMutation({
		mutationKey: ["createTransferRequestStatus", locationId],
		mutationFn: async (input: IMappedTransferRequest[]) => {
			return createTransferRequestStatus(locationId, input);
		},
	});

	const titleText = transferType ? `${transferType} Request` : "Transfer Request";

	const isSwap = transferType === "Asset Swap";

	const { locations, isLoading: isLoadingLocations } = useLocations(getTenantParameters(locationId)?.tenant);
	const franchisesByLocations = locations?.map((item) => ({ franchises: item.franchises, locationId: item.locationId })) ?? [];
	const mappedLocations = locations?.map((item) => ({ label: item.name, value: item.locationId })) ?? [];

	const handleOnCreate = (filters: Partial<TSearchTransferRequest>, data?: IInventoryTradeVehicles, cleaner?: () => void) => {
		if (data) {
			const {
				locationId: originLocationId,
				stock,
				vin,
				make,
				model,
				trim,
				colorExterior,
				year,
				type,
				_id,
				transferAmount,
				status,
			} = data;
			const input = [
				{
					transferType,
					originLocationId,
					destinationLocationId: filters?.location?.find((loc) => loc.group === "destination")?.value ?? "",
					vehicle: {
						stockNumber: stock,
						vinNumber: vin,
						year: Number(year),
						_id,
						make,
						model,
						trim,
						status,
						colorExterior,
						type,
						transferAmount,
					},
				},
			];
			mutate(input, {
				onSuccess: () => {
					cleaner?.();
				},
				onError: () => {
					cleaner?.();
				},
			});
		}
	};

	const defaultSearchTransferRequest = resolveDefaultTransferRequest(uniqueVinNumbers, [{ value: locationId, group: "origin" }]);

	return (
		<>
			<Box className="pt-7">
				<Typography component={"h2"} variant={"h2"}>
					{titleText}
				</Typography>
			</Box>
			<StartTransferRequest
				transferType={transferType}
				setTransferType={setTransferType}
				permission={editPermission}
				filterItems={filterItems}
				isCurrentPayPeriod={isCurrentPayPeriod}
			/>
			{isSwap ? (
				<SwapRequest
					{...props}
					transferType={transferType}
					setTransferType={setTransferType}
					locationsOptions={{ mappedLocations, isLoadingLocations, franchisesByLocations }}
					isCreatingTransferRequest={isPending}
					mutate={mutate}
					uniqueVinNumbers={uniqueVinNumbers}
					isCurrentPayPeriod={isCurrentPayPeriod}
				/>
			) : (
				<TransferRequestAction
					{...props}
					table="createTransferRequest"
					transferType={transferType}
					iconType={transferType}
					setTransferType={setTransferType}
					wrapperClasses="pt-[32px]"
					locationsOptions={{ mappedLocations, isLoadingLocations, franchisesByLocations }}
					onCreate={handleOnCreate}
					isCreatingTransferRequest={isPending}
					defaultSearchTransferRequest={defaultSearchTransferRequest}
					isCurrentPayPeriod={isCurrentPayPeriod}
				/>
			)}
		</>
	);
}
